import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckIcon, SmartphoneIcon, SparklesIcon } from 'lucide-react';
import { images, innerGenius, screenRoutine } from '../data/landing';
type Mode = 'screens' | 'genius';
export function ProblemSolution() {
  const [mode, setMode] = useState<Mode>('screens');
  const isScreens = mode === 'screens';
  const items = isScreens ? screenRoutine : innerGenius;
  return <section className="w-full bg-white py-20 md:py-28" aria-labelledby="problem-title" data-id="element-187" data-mp="iba16agkfyn7">
      <div className="mx-auto max-w-6xl px-5" data-id="element-188" data-mp="eazv6xsrx88c">
        <div className="mx-auto max-w-3xl text-center" data-id="element-189" data-mp="9jhy4ktvfrsn">
          <h2 id="problem-title" className="font-display text-3xl font-bold leading-tight text-ink md:text-5xl" data-id="element-190" data-mp="9hx3ywbgyavb">
            Is your child's true potential trapped behind a screen?
          </h2>
          <p className="mt-8 font-display text-7xl font-bold leading-none text-berry md:text-8xl" data-id="element-191" data-mp="121dfu39144sw">6+ hrs</p>
          <p className="mx-auto mt-3 max-w-xl text-lg font-semibold text-inkSoft" data-id="element-192" data-mp="x798vjzrjwrt">
            a day of screen time for the average kid. Smartphones, tablets and video games are replacing creative play and real-world learning.
          </p>
        </div>

        <div className="mt-12 flex justify-center" data-id="element-193" data-mp="3u6efoiccwbim">
          <div role="tablist" aria-label="Compare routines" className="inline-flex rounded-full bg-cream p-1.5 ring-1 ring-line" data-id="element-194" data-mp="3u3s9on54642i">
            {([{
            id: 'screens',
            label: 'The screen-time routine',
            icon: SmartphoneIcon
          }, {
            id: 'genius',
            label: 'Unlocking their inner genius',
            icon: SparklesIcon
          }] as const).map(({
            id,
            label,
            icon: Icon
          }) => {
            const active = mode === id;
            return <button key={id} role="tab" aria-selected={active} onClick={() => setMode(id)} className="relative flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 font-display text-sm font-semibold focus:outline-none focus-visible:ring-4 focus-visible:ring-teal/30 sm:px-6 sm:text-base" data-id="element-195" data-mp="3f9dqalhn0snb">
                  {active && <motion.span layoutId="tab-pill" className={`absolute inset-0 rounded-full shadow-soft ${id === 'screens' ? 'bg-ink' : 'bg-teal'}`} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-196" data-mp="2hh5vshv5amf2" />}
                  <Icon className={`relative h-4 w-4 ${active ? 'text-white' : 'text-inkSoft'}`} aria-hidden data-id="element-197" data-mp="31329wpu1x26s" />
                  <span className={`relative ${active ? 'text-white' : 'text-inkSoft'}`} data-id="element-198" data-mp="2wbk4npkzwfg0">
                    <span className="sm:hidden" data-id="element-199" data-mp="2rd909udgyx56">{id === 'screens' ? 'Screen time' : 'Inner genius'}</span>
                    <span className="hidden sm:inline" data-id="element-200" data-mp="3oswmr7edeqr1">{label}</span>
                  </span>
                </button>;
          })}
          </div>
        </div>

        <div className="mx-auto mt-12 grid max-w-5xl items-center gap-10 lg:grid-cols-2" data-id="element-201" data-mp="3rcswffawpnw3">
          <div className="relative aspect-[4/3] overflow-hidden rounded-[32px] bg-cream shadow-lift" data-id="element-202" data-mp="3rf5mkfqtj4k8">
            <AnimatePresence mode="wait" initial={false} data-id="element-203" data-mp="8cm5e3un9gr">
              <motion.img key={mode} src={isScreens ? images.screens : images.building} alt={isScreens ? 'Two kids staring at smartphones on a couch' : 'Two kids happily building a robot together'} className={`absolute inset-0 h-full w-full object-cover ${isScreens ? 'grayscale-[60%]' : ''}`} initial={{
              opacity: 0,
              scale: 1.03
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-204" data-mp="54tdeh6ci3y2" />
            </AnimatePresence>
            <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-sm font-extrabold text-white ${isScreens ? 'bg-ink' : 'bg-teal'}`} data-id="element-205" data-mp="wyljwtx80b3z">
              {isScreens ? 'Before' : 'After Maker Lab™'}
            </span>
          </div>

          <div data-id="element-206" data-mp="k82viywy69ge">
            <h3 className="font-display text-3xl font-bold text-ink" data-id="element-207" data-mp="k8cjyi2cg8jz">
              {isScreens ? 'The screen-time routine' : 'Unlocking their inner genius'}
            </h3>
            <AnimatePresence mode="wait" initial={false} data-id="element-208" data-mp="t04tmqjjndni">
              <motion.ul key={mode} className="mt-6 space-y-4" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-209" data-mp="t0mxa88jcu5g">
                {items.map(item => <li key={item} className="flex items-start gap-3 text-lg font-semibold text-ink" data-id="element-210" data-mp="a93t3y2rza1u">
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${isScreens ? 'bg-blush text-berry' : 'bg-mint text-teal'}`} data-id="element-211" data-mp="1qmkabq45c7a0">
                      {isScreens ? <XIcon className="h-4 w-4" strokeWidth={3} aria-hidden data-id="element-212" data-mp="33lnizyvq5rim" /> : <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden data-id="element-213" data-mp="33o9rc0qfwb1w" />}
                    </span>
                    {item}
                  </li>)}
              </motion.ul>
            </AnimatePresence>
            {isScreens && <button onClick={() => setMode('genius')} className="mt-8 font-display text-lg font-semibold text-teal underline decoration-2 underline-offset-4 hover:text-ink" data-id="element-214" data-mp="2zllj9v9jv85z">
                See what changes with Maker Lab™ →
              </button>}
          </div>
        </div>
      </div>
    </section>;
}